import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { getRankInfo, getNextRankProgress } from '../utils/rankUtils';
import { getRankFrameStyle, getBioCharLimit, hasVerifiedBadge } from '../utils/rankFeatures';
import { supabase } from '../lib/supabase';
import { activityService } from '../services/activityService';
import './Profile.css';

interface ProfileProps {
  onNavigate: (page: string) => void;
  showToast?: (message: string, type?: 'success' | 'error' | 'info' | 'warning') => void;
}

const Profile: React.FC<ProfileProps> = ({ onNavigate, showToast }) => {
  const { user, logout, updateUser } = useAuth();
  const { notificationsEnabled, setNotificationsEnabled } = useNotifications();
  const [activeTab, setActiveTab] = useState<'profile' | 'stats' | 'settings'>('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(user?.name || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [savingProfile, setSavingProfile] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(user?.avatarUrl || '');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [creatingBucket, setCreatingBucket] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Settings states
  const [darkMode, setDarkMode] = useState(document.body.classList.contains('dark-mode'));
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  if (!user) return null;

  const rankInfo = getRankInfo(user.rank);
  const nextRank = getNextRankProgress(user.points, user.rank);

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      // Update profile data (name and bio only in profiles table)
      const { error } = await supabase
        .from('profiles')
        .update({ 
          name: editedName,
          bio: bio
        })
        .eq('id', user?.id);

      if (error) throw error;
      
      // Update user context
      updateUser({ 
        name: editedName, 
        bio: bio 
      });
      
      setIsEditing(false);
      if (showToast) {
        showToast('Profile updated successfully!', 'success');
      }
    } catch (error) {
      if (showToast) {
        showToast('Failed to update profile', 'error');
      }
    } finally {
      setSavingProfile(false);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0] || !user) return;
    
    const file = e.target.files[0];
    if (file.size > 5 * 1024 * 1024) {
      if (showToast) {
        showToast('File size must be less than 5MB', 'error');
      }
      return;
    }

    setUploadingAvatar(true);
    try {
      // Delete old avatar if exists
      if (avatarUrl) {
        // Extract the full path from the URL (user_id/filename)
        const urlParts = avatarUrl.split('/');
        const oldFileName = urlParts.slice(-2).join('/'); // Gets last 2 parts: user_id/filename
        if (oldFileName && oldFileName.includes('/')) {
          await supabase.storage.from('avatars').remove([oldFileName]);
        }
      }

      // Upload new avatar - must be in user's folder for RLS policy
      const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const fileName = `${user.id}/${user.id}_${Date.now()}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, {
          upsert: true,
          contentType: file.type
        });

      if (error) {
        throw error;
      }
      

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      // Update profile with new avatar URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setAvatarUrl(publicUrl);
      // Update the user context with new avatar URL
      updateUser({ avatarUrl: publicUrl });
      if (showToast) {
        showToast('Avatar uploaded successfully!', 'success');
      }
    } catch (error: any) {
      if (error.message?.includes('Bucket not found')) {
        const shouldCreate = window.confirm('Avatar storage needs to be set up. Would you like to set it up now?');
        if (shouldCreate) {
          setCreatingBucket(true);
          const result = await activityService.createStorageBuckets();
          setCreatingBucket(false);
          if (result) {
            if (showToast) {
              showToast('Storage set up successfully! Please try uploading your avatar again.', 'success');
            }
          } else {
            if (showToast) {
              showToast('Failed to set up storage. Please contact support.', 'error');
            }
          }
        }
      } else {
        if (showToast) {
          showToast('Failed to upload avatar', 'error');
        }
      }
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleLogout = async () => {
    // Navigate first to prevent seeing dashboard
    onNavigate('login');
    // Then logout
    await logout();
  };

  const toggleDarkMode = () => {
    document.body.classList.toggle('dark-mode');
    setDarkMode(!darkMode);
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    
    // Validate inputs
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }
    
    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    setChangingPassword(true);
    
    try {
      // First, verify current password by re-authenticating
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });
      
      if (signInError) {
        setPasswordError('Current password is incorrect');
        setChangingPassword(false);
        return;
      }
      
      // Update password
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (error) throw error;
      
      // Success
      if (showToast) {
        showToast('Password changed successfully!', 'success');
      }
      setShowPasswordModal(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setPasswordError('Failed to change password. Please try again.');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="profile-page">
      <div className="profile-header">
        <button onClick={() => onNavigate('dashboard')} className="back-btn">
          ← Back
        </button>
      </div>

      <div className="profile-tabs">
        <button
          className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          <span className="tab-icon">👤</span>
          Profile
        </button>
        <button
          className={`tab ${activeTab === 'stats' ? 'active' : ''}`}
          onClick={() => setActiveTab('stats')}
        >
          <span className="tab-icon">📊</span>
          Stats
        </button>
        <button
          className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <span className="tab-icon">⚙️</span>
          Settings
        </button>
      </div>

      <div className="profile-content">
        {activeTab === 'profile' && (
          <div className="profile-section">
            <div className="profile-card">
              <div className="profile-avatar">
                <div className="avatar-frame" style={getRankFrameStyle(user.rank)}>
                  {avatarUrl || user.avatarUrl ? (
                    <img src={avatarUrl || user.avatarUrl} alt={user.name} className="avatar-image" />
                  ) : (
                    <div className="avatar-large">{user.name[0]}</div>
                  )}
                  <button 
                    className="avatar-upload-btn" 
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingAvatar || creatingBucket}
                    title="Change profile picture"
                  >
                    {uploadingAvatar || creatingBucket ? (
                      <span className="upload-spinner">⟳</span>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                        <circle cx="12" cy="13" r="4"/>
                      </svg>
                    )}
                  </button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  style={{ display: 'none' }}
                />
                <div className="rank-badge-large" style={{ background: rankInfo.color }}>
                  <span>{rankInfo.icon}</span>
                  <span>{rankInfo.name}</span>
                  {hasVerifiedBadge(user.rank) && <span className="verified-badge">✓</span>}
                </div>
              </div>

              <div className="profile-info">
                {!isEditing ? (
                  <>
                    <h2>{user.name}</h2>
                    <p className="roll-no">{user.rollNo}</p>
                    <p className="department">{user.department}</p>
                    <p className="email">{user.email}</p>
                    {bio && <p className="bio">{bio}</p>}
                    <button onClick={() => setIsEditing(true)} className="edit-btn">
                      Edit
                    </button>
                  </>
                ) : (
                  <div className="edit-form">
                    <input
                      type="text"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      placeholder="Name"
                      className="edit-input"
                    />
                    <input
                      type="text"
                      value={user.rollNo}
                      disabled
                      className="edit-input disabled"
                      title="Roll number cannot be changed"
                    />
                    <input
                      type="text"
                      value={user.department}
                      disabled
                      className="edit-input disabled"
                      title="Department cannot be changed"
                    />
                    <input
                      type="email"
                      value={user.email}
                      disabled
                      className="edit-input disabled"
                      title="Email cannot be changed"
                    />
                    <textarea
                      value={bio}
                      onChange={(e) => setBio(e.target.value)}
                      placeholder={`Bio (max ${getBioCharLimit(user.rank)} characters)`}
                      maxLength={getBioCharLimit(user.rank)}
                      className="edit-input bio-input"
                      rows={3}
                    />
                    <span className="char-count">{bio.length}/{getBioCharLimit(user.rank)}</span>
                    <p className="edit-note">Note: Email, roll number and department cannot be changed</p>
                    <div className="edit-actions">
                      <button onClick={handleSaveProfile} className="save-btn" disabled={savingProfile}>
                        {savingProfile ? 'Saving...' : 'Save'}
                      </button>
                      <button onClick={() => {
                        setIsEditing(false);
                        setEditedName(user.name);
                        setBio(user.bio || '');
                      }} className="cancel-btn" disabled={savingProfile}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="member-since">
              <p>🗓️ Member since {new Date(user.joinedAt).toLocaleDateString()}</p>
            </div>
          </div>
        )}

        {activeTab === 'stats' && (
          <div className="stats-section">
            <div className="rank-progress">
              <h3>Rank Progress</h3>
              <div className="current-rank">
                <span className="rank-icon" style={{ color: rankInfo.color }}>
                  {rankInfo.icon}
                </span>
                <span>{rankInfo.name}</span>
              </div>
              {nextRank.pointsNeeded > 0 && (
                <>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${nextRank.progress}%` }}
                    ></div>
                  </div>
                  <p className="progress-text">
                    {nextRank.pointsNeeded} points to next rank
                  </p>
                </>
              )}
            </div>

            <div className="activity-breakdown">
              <h3>Activity Breakdown</h3>
              <div className="breakdown-item">
                <span>📸 Before/After Photos</span>
                <span>{user.beforeAfterCount}</span>
              </div>
              <div className="breakdown-item">
                <span>🎬 Videos</span>
                <span>{user.videoCount}</span>
              </div>
              <div className="breakdown-item">
                <span>📅 Login Streak</span>
                <span>{user.loginStreak} days</span>
              </div>
            </div>

            <div className="stats-overview">
              <div className="stats-row">
                <div className="stat-box">
                  <div className="stat-icon">🏆</div>
                  <div className="stat-value">{user.points}</div>
                  <div className="stat-label">Total Points</div>
                </div>
                <div className="stat-box">
                  <div className="stat-icon">🎯</div>
                  <div className="stat-value">{user.totalActivities}</div>
                  <div className="stat-label">Activities</div>
                </div>
              </div>
              <div className="stats-row">
                <div className="stat-box">
                  <div className="stat-icon">🔥</div>
                  <div className="stat-value">{user.streak}</div>
                  <div className="stat-label">Day Streak</div>
                </div>
                <div className="stat-box">
                  <div className="stat-icon">🏅</div>
                  <div className="stat-value">{user.achievements.length}</div>
                  <div className="stat-label">Achievements</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="settings-section">
            <div className="settings-group">
              <h3>Appearance</h3>
              <div className="setting-item">
                <div>
                  <h4>Dark Mode</h4>
                  <p>Use dark theme across the app</p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={darkMode}
                    onChange={toggleDarkMode}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            <div className="settings-group">
              <h3>Notifications</h3>
              <div className="setting-item">
                <div>
                  <h4>In-App Notifications</h4>
                  <p>Show notifications for achievements and reactions</p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notificationsEnabled}
                    onChange={(e) => setNotificationsEnabled(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>


            <div className="settings-group">
              <h3>Account</h3>
              <button 
                className="danger-btn"
                onClick={() => setShowPasswordModal(true)}
              >
                Change Password
              </button>
              <button onClick={handleLogout} className="danger-btn">
                Logout
              </button>
            </div>

          </div>
        )}
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="password-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Change Password</h2>
              <button className="close-btn" onClick={() => setShowPasswordModal(false)}>×</button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label>Current Password</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="form-input"
                />
              </div>
              
              {passwordError && (
                <div className="error-message">
                  {passwordError}
                </div>
              )}
              
              <div className="modal-actions">
                <button 
                  className="cancel-btn" 
                  onClick={() => setShowPasswordModal(false)}
                  disabled={changingPassword}
                >
                  Cancel
                </button>
                <button 
                  className="save-btn" 
                  onClick={handleChangePassword}
                  disabled={changingPassword}
                >
                  {changingPassword ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;